import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarRange, Download, RefreshCw, X } from "lucide-react";
import type {
  Paginated,
  PerformanceRange,
  PerformanceNetworkScopePayload,
  PerformanceTrackerPayload,
  ProductDto,
  SupplyRoute,
  TrendGranularity,
  UserDto,
} from "@gatiq/shared";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  FilterSelect,
  Modal,
  Page,
  PageBody,
  PageHeader,
  Pagination,
  SearchInput,
  Skeleton,
  StatCard,
  Table,
  Tabs,
  TD,
  TDStacked,
  TH,
  THead,
  TR,
  statusTone,
  useToast,
} from "@/ui";
import { api, ApiClientError, freshSession } from "@/lib/api";
import { localeOf, useLanguage } from "@/i18n/language";
import { BarList, Heatmap, TrendChart, type TrendDatum } from "@/components/charts";
import { ProductPerformanceFlow } from "@/components/ProductPerformanceFlow";

/**
 * The org admin's performance dashboard.
 *
 * One filter row sits above everything and scopes all of it — every tile,
 * chart and table re-renders against the same slice, so the numbers agree.
 * Charts are single-series in the org's brand colour; the story is volume
 * over time, where it comes from, and when it happens, with the detail
 * tables underneath as the table view of each.
 *
 * Dates are IST days (see the API), and the date range leads the filters
 * because it is the one every reader reaches for first.
 */

const EN = {
  title: "Performance",
  exportCsv: "Export CSV",
  refresh: "Refresh",
  today: "Today",
  d7: "7 days",
  d30: "30 days",
  d90: "90 days",
  all: "All time",
  custom: "Custom",
  from: "From",
  to: "To",
  apply: "Apply",
  product: "Product",
  counter: "Counter",
  allProducts: "All products",
  allCounters: "All counters",
  search: "Search karigar, phone or code…",
  searchLabel: "Search scans",
  karigar: "Karigar",
  clear: "Clear filters",
  allTimeCaption: "All scans to date",
  vsPrevious: "vs the previous period",
  noChange: "no change",
  newValue: "new",
  kpiScans: "Scans",
  kpiPoints: "QCoin awarded",
  kpiKarigars: "Active karigars",
  kpiCounters: "Active counters",
  kpiNew: "New karigars",
  kpiNewHint: "registered in this period",
  kpiAvg: (n: string) => `${n} QCoin per scan`,
  trendTitle: "Scan activity",
  pointsTrendTitle: "QCoin awarded over time",
  perHour: "Per hour, today",
  perDay: "Per day",
  perWeek: "Per week",
  metricScans: "Scans",
  metricPoints: "QCoin",
  metricLabel: "View by",
  chart: "Chart",
  table: "Table",
  period: "Period",
  rowScans: "Scans",
  rowPoints: "QCoin",
  rowKarigars: "Karigars",
  noScans: "No scans in this period",
  noScansHint: "Try a longer date range or clear the filters.",
  routesTitle: "Supply routes",
  route: {
    org: "Factory direct",
    depot: "Depot",
    distributor: "Distributor",
    sub_distributor: "Sub-distributor",
    unassigned: "No counter",
  } satisfies Record<SupplyRoute, string>,
  topProducts: "Top products",
  topCounters: "Top counters",
  topKarigars: "Top karigars",
  nothingYet: "Nothing in this period.",
  heatTitle: "When karigars scan",
  fewer: "Fewer",
  more: "More",
  weekdays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  weekdaysLong: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  heatCell: (day: string, hour: number, n: string) =>
    `${day} ${String(hour).padStart(2, "0")}:00–${String(hour + 1).padStart(2, "0")}:00 · ${n} scans`,
  detailTitle: "Details",
  tabKarigars: "Karigars",
  tabCounters: "Counters",
  tabNetwork: "Network",
  tabProducts: "Products",
  tabScans: "Scan log",
  colRank: "#",
  colKarigar: "Karigar",
  colCounter: "Counter",
  colKyc: "KYC",
  colScans: "Scans",
  colPoints: "QCoin",
  colLast: "Last scan",
  colRoute: "Supply route",
  colKarigars: "Karigars",
  colCommission: "Commission",
  colProduct: "Product",
  colPerCode: "Per code",
  colIssued: "Issued",
  colRate: "Scan rate",
  colTime: "Time",
  colCode: "Code",
  approved: "Approved",
  pending: "Pending",
  rejected: "Rejected",
  direct: "Direct / unassigned",
  loadFailed: "Could not load performance data.",
  exportSuccess: "Report downloaded.",
  exportFailed: "Could not download the report. Please try again.",
};

type Strings = typeof EN;

const HI: Strings = {
  title: "प्रदर्शन",
  exportCsv: "CSV डाउनलोड",
  refresh: "रिफ्रेश",
  today: "आज",
  d7: "7 दिन",
  d30: "30 दिन",
  d90: "90 दिन",
  all: "शुरू से",
  custom: "कस्टम",
  from: "से",
  to: "तक",
  apply: "लागू करें",
  product: "उत्पाद",
  counter: "काउंटर",
  allProducts: "सभी उत्पाद",
  allCounters: "सभी काउंटर",
  search: "कारीगर, मोबाइल या कोड खोजें…",
  searchLabel: "स्कैन खोजें",
  karigar: "कारीगर",
  clear: "फ़िल्टर हटाएँ",
  allTimeCaption: "अब तक के सभी स्कैन",
  vsPrevious: "पिछली अवधि की तुलना में",
  noChange: "कोई बदलाव नहीं",
  newValue: "नया",
  kpiScans: "स्कैन",
  kpiPoints: "दिए गए QCoin",
  kpiKarigars: "सक्रिय कारीगर",
  kpiCounters: "सक्रिय काउंटर",
  kpiNew: "नए कारीगर",
  kpiNewHint: "इस अवधि में जुड़े",
  kpiAvg: (n: string) => `प्रति स्कैन ${n} QCoin`,
  trendTitle: "स्कैन गतिविधि",
  pointsTrendTitle: "समय के साथ दिए गए QCoin",
  perHour: "आज, प्रति घंटा",
  perDay: "प्रति दिन",
  perWeek: "प्रति सप्ताह",
  metricScans: "स्कैन",
  metricPoints: "QCoin",
  metricLabel: "इसके अनुसार देखें",
  chart: "चार्ट",
  table: "तालिका",
  period: "अवधि",
  rowScans: "स्कैन",
  rowPoints: "QCoin",
  rowKarigars: "कारीगर",
  noScans: "इस अवधि में कोई स्कैन नहीं",
  noScansHint: "लंबी अवधि चुनें या फ़िल्टर हटाएँ।",
  routesTitle: "आपूर्ति माध्यम",
  route: {
    org: "फैक्ट्री से सीधे",
    depot: "डिपो",
    distributor: "डिस्ट्रिब्यूटर",
    sub_distributor: "सब-डिस्ट्रिब्यूटर",
    unassigned: "कोई काउंटर नहीं",
  },
  topProducts: "शीर्ष उत्पाद",
  topCounters: "शीर्ष काउंटर",
  topKarigars: "शीर्ष कारीगर",
  nothingYet: "इस अवधि में कुछ नहीं।",
  heatTitle: "कारीगर कब स्कैन करते हैं",
  fewer: "कम",
  more: "ज़्यादा",
  weekdays: ["सोम", "मंगल", "बुध", "गुरु", "शुक्र", "शनि", "रवि"],
  weekdaysLong: ["सोमवार", "मंगलवार", "बुधवार", "गुरुवार", "शुक्रवार", "शनिवार", "रविवार"],
  heatCell: (day: string, hour: number, n: string) =>
    `${day} ${String(hour).padStart(2, "0")}:00–${String(hour + 1).padStart(2, "0")}:00 · ${n} स्कैन`,
  detailTitle: "विवरण",
  tabKarigars: "कारीगर",
  tabCounters: "काउंटर",
  tabNetwork: "नेटवर्क",
  tabProducts: "उत्पाद",
  tabScans: "स्कैन लॉग",
  colRank: "क्र.",
  colKarigar: "कारीगर",
  colCounter: "काउंटर",
  colKyc: "KYC",
  colScans: "स्कैन",
  colPoints: "QCoin",
  colLast: "अंतिम स्कैन",
  colRoute: "आपूर्ति माध्यम",
  colKarigars: "कारीगर",
  colCommission: "कमीशन",
  colProduct: "उत्पाद",
  colPerCode: "प्रति कोड",
  colIssued: "जारी",
  colRate: "स्कैन दर",
  colTime: "समय",
  colCode: "कोड",
  approved: "स्वीकृत",
  pending: "लंबित",
  rejected: "अस्वीकृत",
  direct: "सीधे / अनिर्धारित",
  loadFailed: "प्रदर्शन डेटा लोड नहीं हो सका।",
  exportSuccess: "रिपोर्ट डाउनलोड हो गई।",
  exportFailed: "रिपोर्ट डाउनलोड नहीं हो सकी। कृपया फिर कोशिश करें।",
};

const STRINGS = { en: EN, hi: HI };

const TZ = "Asia/Kolkata";
const PAGE_SIZE = 25;
type Tab = "karigars" | "counters" | "network" | "products" | "scans";
type Drilldown = { focus: "product" | "karigar" | "counter" | "network"; productId: string; karigar: { id: string; name: string } | null; counterId: string; networkMember?: { id: string; name: string; roleLabel: string } };

export function PerformanceTrackerPage() {
  const toast = useToast();
  const { lang } = useLanguage();
  const t = STRINGS[lang];
  const locale = localeOf(lang);

  const num = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  const compact = useMemo(
    () => new Intl.NumberFormat(locale, { notation: "compact", maximumFractionDigits: 1 }),
    [locale],
  );
  const dateFmt = useCallback(
    (iso: string, opts: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(locale, { timeZone: TZ, ...opts }).format(new Date(iso)),
    [locale],
  );

  /* ── filters ─────────────────────────────────────────────── */
  const [range, setRange] = useState<PerformanceRange>("30d");
  const [draft, setDraft] = useState({ from: "", to: "" });
  const [custom, setCustom] = useState<{ from: string; to: string } | null>(null);
  const [productId, setProductId] = useState("");
  const [counterId, setCounterId] = useState("");
  const [karigar, setKarigar] = useState<{ id: string; name: string } | null>(null);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [metric, setMetric] = useState<"scans" | "points">("scans");

  function selectProduct(id: string): void {
    setProductId(id);
    setKarigar(null);
    setCounterId("");
  }
  function selectKarigar(value: { id: string; name: string } | null): void {
    setKarigar(value);
    setCounterId("");
  }

  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  const params = useMemo(() => {
    if (range === "custom" && !custom) return null;
    const p = new URLSearchParams({ range, metric });
    if (range === "custom" && custom) {
      p.set("from", custom.from);
      p.set("to", custom.to);
    }
    if (productId) p.set("productId", productId);
    if (counterId) p.set("counterId", counterId);
    if (karigar) p.set("khatiId", karigar.id);
    if (q) p.set("q", q);
    return p;
  }, [range, custom, productId, counterId, karigar, q, metric]);

  // The scan log's page belongs to one filter set: change the filters and it
  // is page 1 again, without a second fetch for the page that no longer fits.
  const filterKey = params?.toString() ?? "";
  const [pageState, setPageState] = useState({ key: "", page: 1 });
  const page = pageState.key === filterKey ? pageState.page : 1;

  /* ── data ────────────────────────────────────────────────── */
  const [data, setData] = useState<PerformanceTrackerPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [counters, setCounters] = useState<UserDto[]>([]);
  const [tab, setTab] = useState<Tab>("karigars");
  const [trendView, setTrendView] = useState<"chart" | "table">("chart");
  const seq = useRef(0);
  const [drilldown, setDrilldown] = useState<Drilldown | null>(null);
  const [drilldownData, setDrilldownData] = useState<PerformanceTrackerPayload | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState<string | null>(null);
  const [drilldownRetry, setDrilldownRetry] = useState(0);
  const drilldownSeq = useRef(0);
  const [networkScopeData, setNetworkScopeData] = useState<PerformanceNetworkScopePayload | null>(null);
  const [networkScopeLoading, setNetworkScopeLoading] = useState(false);
  const [networkScopeError, setNetworkScopeError] = useState<string | null>(null);
  const [networkScopePageState, setNetworkScopePageState] = useState({ key: "", page: 1 });
  const networkScopeSeq = useRef(0);

  // Filter options come from the catalogue, not from the current results —
  // otherwise choosing a counter would leave only that counter to choose.
  useEffect(() => {
    api
      .get<{ items: ProductDto[] }>("/org/products")
      .then((r) => setProducts(r.items))
      .catch(() => undefined);
    api
      .get<Paginated<UserDto>>("/org/counters?page=1&pageSize=100")
      .then((r) => setCounters(r.items))
      .catch(() => undefined);
  }, []);

  const load = useCallback(async () => {
    if (!params) return;
    const mine = ++seq.current;
    setLoading(true);
    try {
      const p = new URLSearchParams(params);
      p.set("page", String(page));
      p.set("pageSize", String(PAGE_SIZE));
      const res = await api.get<PerformanceTrackerPayload>(`/org/performance?${p.toString()}`);
      if (mine === seq.current) setData(res);
    } catch (err) {
      if (mine === seq.current) toast.error(err instanceof ApiClientError ? err.message : t.loadFailed);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [params, page, toast, t.loadFailed]);

  useEffect(() => {
    void load();
  }, [load]);

  // Drilldown has its own selection and request. Opening a person or product
  // never replaces the dashboard's totals, leaders, charts, or tables.
  const drilldownParams = useMemo(() => {
    if (!drilldown || (range === "custom" && !custom)) return null;
    const p = new URLSearchParams({ range, metric, page: "1", pageSize: "1" });
    if (range === "custom" && custom) {
      p.set("from", custom.from);
      p.set("to", custom.to);
    }
    if (q) p.set("q", q);
    if (drilldown.productId) p.set("productId", drilldown.productId);
    if (drilldown.karigar) p.set("khatiId", drilldown.karigar.id);
    if (drilldown.counterId) p.set("counterId", drilldown.counterId);
    if (drilldown.networkMember) p.set("networkMemberId", drilldown.networkMember.id);
    return p.toString();
  }, [drilldown, range, custom, q, metric]);

  useEffect(() => {
    if (!drilldownParams) return;
    const mine = ++drilldownSeq.current;
    setDrilldownLoading(true);
    setDrilldownError(null);
    void api.get<PerformanceTrackerPayload>(`/org/performance?${drilldownParams}`)
      .then((result) => { if (mine === drilldownSeq.current) setDrilldownData(result); })
      .catch((error: unknown) => {
        if (mine === drilldownSeq.current) setDrilldownError(error instanceof ApiClientError ? error.message : t.loadFailed);
      })
      .finally(() => { if (mine === drilldownSeq.current) setDrilldownLoading(false); });
    return () => { drilldownSeq.current++; };
  }, [drilldownParams, drilldownRetry, t.loadFailed]);

  const networkScopeKey = drilldown?.networkMember
    ? `${drilldown.networkMember.id}:${drilldown.counterId}`
    : "";
  const networkScopePage = networkScopePageState.key === networkScopeKey ? networkScopePageState.page : 1;
  const networkScopeParams = useMemo(() => {
    if (!drilldown?.networkMember || drilldown.focus !== "network") return null;
    const p = new URLSearchParams({
      networkMemberId: drilldown.networkMember.id,
      page: String(networkScopePage),
      pageSize: "20",
    });
    if (drilldown.counterId) p.set("counterId", drilldown.counterId);
    return p.toString();
  }, [drilldown, networkScopePage]);

  useEffect(() => {
    if (!networkScopeParams) {
      setNetworkScopeData(null);
      setNetworkScopeLoading(false);
      return;
    }
    const mine = ++networkScopeSeq.current;
    setNetworkScopeData(null);
    setNetworkScopeLoading(true);
    setNetworkScopeError(null);
    void api.get<PerformanceNetworkScopePayload>(`/org/performance/network-scope?${networkScopeParams}`)
      .then((result) => { if (mine === networkScopeSeq.current) setNetworkScopeData(result); })
      .catch((error: unknown) => {
        if (mine === networkScopeSeq.current) {
          setNetworkScopeError(error instanceof ApiClientError ? error.message : t.loadFailed);
        }
      })
      .finally(() => { if (mine === networkScopeSeq.current) setNetworkScopeLoading(false); });
    return () => { networkScopeSeq.current++; };
  }, [networkScopeParams, t.loadFailed]);

  function openDrilldown(selection: Drilldown): void {
    const previousScopeKey = drilldown?.networkMember
      ? `${drilldown.networkMember.id}:${drilldown.counterId}`
      : "";
    const nextScopeKey = selection.networkMember
      ? `${selection.networkMember.id}:${selection.counterId}`
      : "";
    if (previousScopeKey !== nextScopeKey) setNetworkScopePageState({ key: nextScopeKey, page: 1 });
    setDrilldownData(null);
    setDrilldown(selection);
  }

  function closeDrilldown(): void {
    drilldownSeq.current++;
    setDrilldown(null);
    setDrilldownData(null);
    setDrilldownLoading(false);
    setDrilldownError(null);
    setNetworkScopeData(null);
    setNetworkScopeError(null);
    setNetworkScopeLoading(false);
  }

  async function exportCsv(): Promise<void> {
    if (!params) return;
    setDownloading(true);
    try {
      await freshSession();
      const res = await fetch(`/api/org/performance/export.csv?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error(String(res.status));
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `performance-${range}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      toast.success(t.exportSuccess);
    } catch {
      toast.error(t.exportFailed);
    } finally {
      setDownloading(false);
    }
  }

  function chooseRange(value: string): void {
    const next = value as PerformanceRange;
    setRange(next);
    if (next !== "custom") setCustom(null);
  }

  const anyFilter = Boolean(productId || counterId || karigar || q);
  function clearFilters(): void {
    setProductId("");
    setCounterId("");
    setKarigar(null);
    setSearch("");
    setQ("");
  }

  /* ── derived ─────────────────────────────────────────────── */
  const summary = data?.summary;
  const prev = data?.previous ?? null;
  const points = data?.trend.points ?? [];
  const granularity: TrendGranularity = data?.trend.granularity ?? "day";

  function delta(cur: number, before: number | undefined) {
    if (before === undefined) return undefined;
    if (before === 0) return cur > 0 ? { value: t.newValue, direction: "up" as const } : undefined;
    const pct = Math.round(((cur - before) / before) * 100);
    if (pct === 0) return { value: t.noChange, direction: "flat" as const };
    return { value: `${Math.abs(pct)}%`, direction: pct > 0 ? ("up" as const) : ("down" as const) };
  }

  const bucketTick = (iso: string) =>
    granularity === "hour"
      ? dateFmt(iso, { hour: "2-digit", minute: "2-digit", hour12: false })
      : dateFmt(iso, { day: "numeric", month: "short" });
  const bucketLabel = (iso: string) =>
    granularity === "hour"
      ? dateFmt(iso, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })
      : granularity === "day"
        ? dateFmt(iso, { weekday: "short", day: "numeric", month: "short", year: "numeric" })
        : `${lang === "hi" ? "सप्ताह" : "Week of"} ${dateFmt(iso, { day: "numeric", month: "short", year: "numeric" })}`;

  const trendData: TrendDatum[] = points.map((p) => ({
    tick: bucketTick(p.bucket),
    label: bucketLabel(p.bucket),
    value: metric === "scans" ? p.scans : p.points,
    rows:
      metric === "scans"
        ? [
            { label: t.rowScans, value: num.format(p.scans) },
            { label: t.rowPoints, value: num.format(p.points) },
            { label: t.rowKarigars, value: num.format(p.karigars) },
          ]
        : [
            { label: t.rowPoints, value: num.format(p.points) },
            { label: t.rowScans, value: num.format(p.scans) },
            { label: t.rowKarigars, value: num.format(p.karigars) },
          ],
  }));
  const spark = (pick: (p: (typeof points)[number]) => number) => points.slice(-14).map(pick);

  const periodCaption = !data
    ? ""
    : data.period.from === null
      ? t.allTimeCaption
      : `${dateFmt(data.period.from, { day: "numeric", month: "short", year: "numeric" })} – ${dateFmt(
          data.period.to,
          { day: "numeric", month: "short", year: "numeric" },
        )}`;

  const metricLabel = metric === "scans" ? t.metricScans : t.metricPoints;
  const routeTotal = (data?.bySupplyRoute ?? []).reduce((n, r) => n + (metric === "scans" ? r.scans : r.points), 0);
  const routes = [...(data?.bySupplyRoute ?? [])].sort((a, b) =>
    metric === "scans" ? b.scans - a.scans : b.points - a.points,
  );
  const topProducts = [...(data?.byProduct ?? [])].sort((a, b) =>
    metric === "scans" ? b.scanCount - a.scanCount : b.totalPointsDistributed - a.totalPointsDistributed,
  ).slice(0, 5);
  const topKarigars = [...(data?.byKhati ?? [])].sort((a, b) =>
    metric === "scans" ? b.scanCount - a.scanCount : b.totalPointsEarned - a.totalPointsEarned,
  ).slice(0, 5);
  const topCounters = [...(data?.byCounter ?? [])].sort((a, b) =>
    metric === "scans" ? b.scanCount - a.scanCount : b.totalPointsEarned - a.totalPointsEarned,
  ).slice(0, 5);
  const networkMembers = (data?.byNetworkMember ?? []).filter((member) => member.role !== "counter" && member.role !== "karigar");
  const lastScan = (iso: string | null) =>
    iso ? dateFmt(iso, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—";
  const kycLabel = (k: string | null) =>
    k === "approved" ? t.approved : k === "rejected" ? t.rejected : k ? t.pending : "—";

  const rangeItems = [
    { value: "today", label: t.today },
    { value: "7d", label: t.d7 },
    { value: "30d", label: t.d30 },
    { value: "90d", label: t.d90 },
    { value: "all", label: t.all },
    { value: "custom", label: t.custom },
  ];
  const todayYmd = new Date(Date.now() + 330 * 60_000).toISOString().slice(0, 10);
  const draftValid = Boolean(draft.from && draft.to && draft.from <= draft.to);
  const refetching = loading && data !== null;

  return (
    <Page fullWidth layout="document">
      <PageHeader
        action={
          <div className="flex items-center gap-2">
            <Button
              aria-label={t.refresh}
              icon={<RefreshCw aria-hidden="true" className={`size-4 ${refetching ? "animate-spin" : ""}`} />}
              onClick={() => void load()}
              size="sm"
              variant="ghost"
            />
            <Button
              disabled={!params}
              icon={<Download aria-hidden="true" className="size-4" />}
              loading={downloading}
              onClick={() => void exportCsv()}
              size="sm"
              variant="secondary"
            >
              {t.exportCsv}
            </Button>
          </div>
        }
        title={t.title}
      />

      {/* Filters and dashboard share the workspace scroll region. */}
      <div className="mb-4 flex shrink-0 flex-col gap-2.5 rounded-lg border-token border-rule bg-raised px-3 py-3 shadow-card">
        <div className="flex flex-wrap items-center gap-2">
          <Tabs items={rangeItems} onChange={chooseRange} value={range} />
          {range === "custom" ? (
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (draftValid) setCustom({ ...draft });
              }}
            >
              <CalendarRange aria-hidden="true" className="size-4 text-muted" />
              <DateInput label={t.from} max={draft.to || todayYmd} onChange={(v) => setDraft((d) => ({ ...d, from: v }))} value={draft.from} />
              <span aria-hidden="true" className="text-muted">
                –
              </span>
              <DateInput label={t.to} max={todayYmd} min={draft.from || undefined} onChange={(v) => setDraft((d) => ({ ...d, to: v }))} value={draft.to} />
              <Button disabled={!draftValid} size="sm" type="submit">
                {t.apply}
              </Button>
            </form>
          ) : null}
          <div className="ml-auto flex w-full flex-wrap items-center justify-between gap-3 sm:w-auto sm:justify-end">
            <div className="flex items-center gap-2" aria-label={t.metricLabel}>
              <span className="text-sm font-medium text-ink">{t.metricLabel}</span>
              <Tabs
                items={[{ value: "scans", label: t.metricScans }, { value: "points", label: t.metricPoints }]}
                onChange={(value) => setMetric(value as "scans" | "points")}
                value={metric}
              />
            </div>
            <div className="w-full sm:w-72">
              <SearchInput label={t.searchLabel} onChange={setSearch} placeholder={t.search} value={search} />
            </div>
          </div>
        </div>

        {/* Dimension filters wrap with the shared filter layout. */}
        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
          <FilterSelect
            allLabel={t.allProducts}
            label={t.product}
            onChange={selectProduct}
            options={products.map((p) => ({ value: p.id, label: `${p.name} · ${p.sku}` }))}
            value={productId}
          />
          <FilterSelect
            allLabel={t.allCounters}
            label={t.counter}
            onChange={setCounterId}
            options={counters.map((c) => ({ value: c.id, label: c.name }))}
            value={counterId}
          />
          {karigar ? (
            <FilterChip label={`${t.karigar}: ${karigar.name}`} onRemove={() => selectKarigar(null)} />
          ) : null}
          {anyFilter ? (
            <button
              className="text-[13px] font-medium text-brand-ink hover:underline"
              onClick={clearFilters}
              type="button"
            >
              {t.clear}
            </button>
          ) : null}
          <span className="ml-auto text-[13px] text-muted">
            {periodCaption}
            {prev && data?.period.from !== null ? <span className="text-muted/80"> · {t.vsPrevious}</span> : null}
          </span>
        </div>
      </div>

      <PageBody className="pb-2">
        {/* Refetch keeps the frame: the previous numbers stay, dimmed, until the new ones land. */}
        <div
          aria-busy={loading}
          className={`flex flex-col gap-4 transition-opacity duration-200 [&>*]:shrink-0 ${refetching ? "opacity-60" : ""}`}
        >
          {/* ── KPIs ── */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5 [&>*:last-child]:col-span-2 md:[&>*:last-child]:col-span-1">
            {summary ? (
              <>
                <StatCard
                  delta={delta(summary.totalScans, prev?.totalScans)}
                  hint={prev ? t.vsPrevious : undefined}
                  label={t.kpiScans}
                  trend={spark((p) => p.scans)}
                  value={compact.format(summary.totalScans)}
                />
                <StatCard
                  delta={delta(summary.totalPoints, prev?.totalPoints)}
                  hint={t.kpiAvg(num.format(summary.avgPointsPerScan))}
                  label={t.kpiPoints}
                  trend={spark((p) => p.points)}
                  value={compact.format(summary.totalPoints)}
                />
                <StatCard
                  delta={delta(summary.activeKhatis, prev?.activeKhatis)}
                  hint={prev ? t.vsPrevious : undefined}
                  label={t.kpiKarigars}
                  trend={spark((p) => p.karigars)}
                  value={num.format(summary.activeKhatis)}
                />
                <StatCard
                  delta={delta(summary.activeCounters, prev?.activeCounters)}
                  hint={prev ? t.vsPrevious : undefined}
                  label={t.kpiCounters}
                  value={num.format(summary.activeCounters)}
                />
                <StatCard hint={t.kpiNewHint} label={t.kpiNew} value={num.format(summary.newKhatis)} />
              </>
            ) : (
              Array.from({ length: 5 }, (_, i) => (
                <div className="rounded-lg border-token border-rule bg-raised px-5 py-4 shadow-card" key={i}>
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="mt-4 h-8 w-24" />
                </div>
              ))
            )}
          </div>

          {/* ── Trend + supply routes ── */}
          <div className="grid gap-4 xl:grid-cols-3">
            <Card className="xl:col-span-2">
              <CardHeader
                action={
                  <div className="flex flex-wrap items-center gap-2">
                    <Tabs
                      items={[
                        { value: "chart", label: t.chart },
                        { value: "table", label: t.table },
                      ]}
                      onChange={(v) => setTrendView(v as "chart" | "table")}
                      value={trendView}
                    />
                  </div>
                }
                description={granularity === "hour" ? t.perHour : granularity === "day" ? t.perDay : t.perWeek}
                title={metric === "scans" ? t.trendTitle : t.pointsTrendTitle}
              />
              {!data ? (
                <div className="px-5 py-5">
                  <Skeleton className="h-[240px] w-full" />
                </div>
              ) : summary && summary.totalScans === 0 ? (
                <EmptyState message={t.noScansHint} title={t.noScans} />
              ) : trendView === "chart" ? (
                <div className="px-2 pt-2 pb-3">
                  <TrendChart
                    ariaLabel={`${metric === "scans" ? t.trendTitle : t.pointsTrendTitle}, ${metricLabel}`}
                    data={trendData}
                    format={(n) => compact.format(n)}
                  />
                </div>
              ) : (
                <div>
                  <Table searchable={false}>
                    <THead>
                      <tr>
                        <TH>{t.period}</TH>
                        <TH align="right">{t.rowScans}</TH>
                        <TH align="right">{t.rowPoints}</TH>
                        <TH align="right">{t.rowKarigars}</TH>
                      </tr>
                    </THead>
                    <tbody>
                      {[...points].reverse().map((p) => (
                        <TR key={p.bucket}>
                          <TD>{bucketLabel(p.bucket)}</TD>
                          <TD align="right" numeric>
                            {num.format(p.scans)}
                          </TD>
                          <TD align="right" numeric>
                            {num.format(p.points)}
                          </TD>
                          <TD align="right" numeric>
                            {num.format(p.karigars)}
                          </TD>
                        </TR>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card>

            <Card>
              <CardHeader title={t.routesTitle} />
              <BarList
                empty={t.nothingYet}
                items={routes.map((r) => ({
                  key: r.route,
                  label: t.route[r.route],
                  value: metric === "scans" ? r.scans : r.points,
                  display: `${num.format(metric === "scans" ? r.scans : r.points)} ${metricLabel} - ${routeTotal ? Math.round(((metric === "scans" ? r.scans : r.points) / routeTotal) * 100) : 0}%`,
                }))}
              />
            </Card>
          </div>

          {/* All three leader groups stay visible while the modal explores one branch. */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
            <Card>
              <CardHeader title={t.topProducts} description={`${num.format(data?.byProduct.length ?? 0)} ${t.tabProducts.toLowerCase()}`} />
              <BarList empty={t.nothingYet} items={topProducts.map((item) => ({
                key: item.productId, label: item.name, sublabel: item.sku, value: metric === "scans" ? item.scanCount : item.totalPointsDistributed,
                display: `${num.format(metric === "scans" ? item.scanCount : item.totalPointsDistributed)} ${metricLabel}`,
              }))} onSelect={(id) => openDrilldown({ focus: "product", productId: id, karigar: null, counterId: "" })} />
            </Card>
            <Card>
              <CardHeader title={t.topKarigars} description={`${num.format(data?.byKhati.length ?? 0)} ${t.tabKarigars.toLowerCase()}`} />
              <BarList empty={t.nothingYet} items={topKarigars.map((item) => ({
                key: item.khatiId, label: item.name, sublabel: item.counterName ?? undefined, value: metric === "scans" ? item.scanCount : item.totalPointsEarned,
                display: `${num.format(metric === "scans" ? item.scanCount : item.totalPointsEarned)} ${metricLabel}`,
              }))} onSelect={(id) => {
                const selected = data?.byKhati.find((item) => item.khatiId === id);
                if (selected) openDrilldown({ focus: "karigar", productId: "", karigar: { id, name: selected.name }, counterId: "" });
              }} />
            </Card>
            <Card>
              <CardHeader title={t.topCounters} description={`${num.format(data?.byCounter.length ?? 0)} ${t.tabCounters.toLowerCase()}`} />
              <BarList empty={t.nothingYet} items={topCounters.map((item) => ({
                key: item.counterId, label: item.name, sublabel: item.supplierName ?? undefined, value: metric === "scans" ? item.scanCount : item.totalPointsEarned,
                display: `${num.format(metric === "scans" ? item.scanCount : item.totalPointsEarned)} ${metricLabel}`,
              }))} onSelect={(id) => openDrilldown({ focus: "counter", productId: "", karigar: null, counterId: id })} />
            </Card>
            {Array.from(new Set(networkMembers.map((member) => member.role + "|" + member.roleLabel)))
              .map((groupKey) => {
                const separator = groupKey.indexOf("|");
                const role = groupKey.slice(0, separator);
                const roleLabel = groupKey.slice(separator + 1);
                const roleMembers = networkMembers
                  .filter((member) => member.role === role && member.roleLabel === roleLabel);
                const members = [...roleMembers]
                  .sort((a, b) => metric === "scans" ? b.scanCount - a.scanCount : b.points - a.points)
                  .slice(0, 5);
                return (
                  <Card key={groupKey}>
                    <CardHeader
                      title={(lang === "hi" ? "शीर्ष " : "Top ") + roleLabel}
                      description={num.format(roleMembers.length) + (roleMembers.length === 1 ? " network member" : " network members")}
                    />
                    <BarList
                      empty={t.nothingYet}
                      items={members.map((member) => {
                        const value = metric === "scans" ? member.scanCount : member.points;
                        return {
                          key: member.id,
                          label: member.name,
                          sublabel: member.level ? roleLabel + " · " + member.level : undefined,
                          value,
                          display: num.format(value) + " " + metricLabel,
                        };
                      })}
                      onSelect={(id) => {
                        const selected = members.find((member) => member.id === id);
                        if (selected) openDrilldown({
                          focus: "network",
                          productId: "",
                          karigar: null,
                          counterId: "",
                          networkMember: { id, name: selected.name, roleLabel: selected.roleLabel },
                        });
                      }}
                    />
                  </Card>
                );
              })}
          </div>

          {/* ── When ── */}
          <Card>
            <CardHeader title={t.heatTitle} />
            {data ? (
              <Heatmap
                describe={(r, h, v) => t.heatCell(t.weekdaysLong[r] ?? "", h, num.format(v))}
                fewer={t.fewer}
                grid={data.heatmap}
                more={t.more}
                rowLabels={t.weekdays}
              />
            ) : (
              <div className="px-5 py-5">
                <Skeleton className="h-40 w-full" />
              </div>
            )}
          </Card>

          {/* ── Detail: the table view of everything above ── */}
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule/70 px-5 py-3">
              <h2 className="font-title text-[15px] font-semibold text-ink">{t.detailTitle}</h2>
              <Tabs
                items={[
                  { value: "karigars", label: t.tabKarigars, count: data?.byKhati.length ?? 0 },
                  { value: "counters", label: t.tabCounters, count: data?.byCounter.length ?? 0 },
                  { value: "network", label: t.tabNetwork, count: networkMembers.length },
                  { value: "products", label: t.tabProducts, count: data?.byProduct.length ?? 0 },
                  { value: "scans", label: t.tabScans, count: data?.totalRecentScans ?? 0 },
                ]}
                onChange={(v) => setTab(v as Tab)}
                value={tab}
              />
            </div>

            {!data ? (
              <div className="px-5 py-5">
                <Skeleton className="h-40 w-full" />
              </div>
            ) : tab === "karigars" ? (
              data.byKhati.length === 0 ? (
                <EmptyState message={t.noScansHint} title={t.noScans} />
              ) : (
                <Table searchable={false}>
                  <THead>
                    <tr>
                      <TH width="3rem">{t.colRank}</TH>
                      <TH>{t.colKarigar}</TH>
                      <TH>{t.colCounter}</TH>
                      <TH>{t.colKyc}</TH>
                      <TH align="right">{t.colScans}</TH>
                      <TH align="right">{t.colPoints}</TH>
                      <TH align="right">{t.colLast}</TH>
                    </tr>
                  </THead>
                  <tbody>
                    {[...data.byKhati].sort((a, b) => metric === "scans" ? b.scanCount - a.scanCount : b.totalPointsEarned - a.totalPointsEarned).map((k, i) => (
                      <TR key={k.khatiId} onClick={() => openDrilldown({ focus: "karigar", productId: "", karigar: { id: k.khatiId, name: k.name }, counterId: "" })}>
                        <TD className="text-muted" numeric>
                          {i + 1}
                        </TD>
                        <TDStacked primary={k.name} secondary={[k.displayId, k.phone].filter(Boolean).join(" · ")} />
                        <TD>{k.counterName ?? t.direct}</TD>
                        <TD>
                          <Badge tone={statusTone(k.kycStatus ?? "")}>{kycLabel(k.kycStatus)}</Badge>
                        </TD>
                        <TD align="right" numeric>
                          {num.format(k.scanCount)}
                        </TD>
                        <TD align="right" numeric>
                          {num.format(k.totalPointsEarned)}
                        </TD>
                        <TD align="right" className="whitespace-nowrap text-muted">
                          {lastScan(k.lastScanAt)}
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              )
            ) : tab === "counters" ? (
              data.byCounter.length === 0 ? (
                <EmptyState message={t.noScansHint} title={t.noScans} />
              ) : (
                <Table searchable={false}>
                  <THead>
                    <tr>
                      <TH>{t.colCounter}</TH>
                      <TH>{t.colRoute}</TH>
                      <TH align="right">{t.colKarigars}</TH>
                      <TH align="right">{t.colScans}</TH>
                      <TH align="right">{t.colPoints}</TH>
                      <TH align="right">{t.colCommission}</TH>
                      <TH align="right">{t.colLast}</TH>
                    </tr>
                  </THead>
                  <tbody>
                    {[...data.byCounter].sort((a, b) => metric === "scans" ? b.scanCount - a.scanCount : b.totalPointsEarned - a.totalPointsEarned).map((c) => (
                      <TR key={c.counterId} onClick={() => openDrilldown({ focus: "counter", productId: "", karigar: null, counterId: c.counterId })}>
                        <TDStacked primary={c.name} secondary={c.displayId ?? undefined} />
                        <TD className="text-muted">{c.supplierName ?? "—"}</TD>
                        <TD align="right" numeric>
                          {num.format(c.uniqueKhatis)}
                        </TD>
                        <TD align="right" numeric>
                          {num.format(c.scanCount)}
                        </TD>
                        <TD align="right" numeric>
                          {num.format(c.totalPointsEarned)}
                        </TD>
                        <TD align="right" numeric>
                          {num.format(c.commissionEarned)}
                        </TD>
                        <TD align="right" className="whitespace-nowrap text-muted">
                          {lastScan(c.lastScanAt)}
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              )
            ) : tab === "network" ? (
              networkMembers.length === 0 ? (
                <EmptyState message={t.noScansHint} title={t.noScans} />
              ) : (
                <Table searchable={false}>
                  <THead>
                    <tr>
                      <TH>{lang === "hi" ? "भूमिका" : "Role / level"}</TH>
                      <TH>{lang === "hi" ? "नाम" : "Name"}</TH>
                      <TH align="right">{t.colScans}</TH>
                      <TH align="right">{t.colPoints}</TH>
                      <TH align="right">{t.colLast}</TH>
                    </tr>
                  </THead>
                  <tbody>
                    {[...networkMembers]
                      .sort((a, b) => metric === "scans" ? b.scanCount - a.scanCount : b.points - a.points)
                      .map((member) => {
                        const roleNames: Record<string, string> = {
                          sales_rep: lang === "hi" ? "सेल्स प्रतिनिधि" : "Sales rep",
                          distributor: lang === "hi" ? "डिस्ट्रीब्यूटर" : "Distributor",
                          sub_distributor: lang === "hi" ? "सब-डिस्ट्रीब्यूटर" : "Sub-distributor",
                          counter: lang === "hi" ? "काउंटर" : "Counter",
                          karigar: lang === "hi" ? "कारीगर" : "Karigar",
                          depot: lang === "hi" ? "डिपो" : "Depot",
                        };
                        const role = member.roleLabel || roleNames[member.role] || member.role;
                        return (
                          <TR key={`${member.role}-${member.id}`} onClick={() => openDrilldown({
                            focus: "network",
                            productId: "",
                            karigar: null,
                            counterId: "",
                            networkMember: { id: member.id, name: member.name, roleLabel: member.roleLabel },
                          })}>
                            <TD>{role}{member.level && member.level > 1 ? ` · L${member.level}` : ""}</TD>
                            <TD className="font-medium">{member.name}</TD>
                            <TD align="right" numeric>{num.format(member.scanCount)}</TD>
                            <TD align="right" numeric>{num.format(member.points)}</TD>
                            <TD align="right" className="whitespace-nowrap text-muted">{lastScan(member.lastScanAt)}</TD>
                          </TR>
                        );
                      })}
                  </tbody>
                </Table>
              )
            ) : tab === "products" ? (
              <Table searchable={false}>
                <THead>
                  <tr>
                    <TH>{t.colProduct}</TH>
                    <TH align="right">{t.colPerCode}</TH>
                    <TH align="right">{t.colIssued}</TH>
                    <TH align="right">{t.colScans}</TH>
                    <TH width="11rem">{t.colRate}</TH>
                    <TH align="right">{t.colPoints}</TH>
                    <TH align="right">{t.colLast}</TH>
                  </tr>
                </THead>
                <tbody>
                  {[...data.byProduct].sort((a, b) => metric === "scans" ? b.scanCount - a.scanCount : b.totalPointsDistributed - a.totalPointsDistributed).map((p) => (
                    <TR key={p.productId} onClick={() => openDrilldown({ focus: "product", productId: p.productId, karigar: null, counterId: "" })}>
                      <TDStacked primary={p.name} secondary={p.sku} />
                      <TD align="right" numeric>
                        {num.format(p.pointsPerCode)}
                      </TD>
                      <TD align="right" numeric>
                        {num.format(p.totalIssued)}
                      </TD>
                      <TD align="right" numeric>
                        {num.format(p.scanCount)}
                      </TD>
                      <TD>
                        <Meter value={p.scanRate} />
                      </TD>
                      <TD align="right" numeric>
                        {num.format(p.totalPointsDistributed)}
                      </TD>
                      <TD align="right" className="whitespace-nowrap text-muted">
                        {lastScan(p.lastScanAt)}
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            ) : data.recentScans.length === 0 ? (
              <EmptyState message={t.noScansHint} title={t.noScans} />
            ) : (
              <>
                <Table searchable={false}>
                  <THead>
                    <tr>
                      <TH>{t.colTime}</TH>
                      <TH>{t.colCode}</TH>
                      <TH>{t.colProduct}</TH>
                      <TH>{t.colKarigar}</TH>
                      <TH>{t.colCounter}</TH>
                      <TH align="right">{t.colPoints}</TH>
                    </tr>
                  </THead>
                  <tbody>
                    {data.recentScans.map((s) => (
                      <TR key={s.id}>
                        <TD className="whitespace-nowrap text-muted">{lastScan(s.scannedAt)}</TD>
                        <TD className="font-mono text-[13px]">{s.code}</TD>
                        <TDStacked primary={s.product?.name ?? "—"} secondary={s.product?.sku ?? undefined} />
                        <TDStacked primary={s.khati.name} secondary={s.khati.phone ?? undefined} />
                        <TD>{s.counter?.name ?? t.direct}</TD>
                        <TD align="right" className="font-medium text-success" numeric>
                          +{num.format(s.points)}
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
                <Pagination
                  onChange={(p) => setPageState({ key: filterKey, page: p })}
                  page={data.page}
                  total={data.totalRecentScans}
                  totalPages={Math.max(1, Math.ceil(data.totalRecentScans / data.pageSize))}
                />
              </>
            )}
          </Card>
        </div>
      </PageBody>
      <Modal
        open={drilldown !== null}
        onClose={closeDrilldown}
        title={lang === "hi" ? "प्रदर्शन का विवरण" : "Performance details"}
        description={periodCaption}
        width="2xl"
      >
        {drilldown ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 text-xs text-muted">
              {drilldown.productId ? <span className="rounded-full bg-brand-light px-3 py-1 text-brand-ink">{t.product}: {products.find((p) => p.id === drilldown.productId)?.name ?? data?.byProduct.find((p) => p.productId === drilldown.productId)?.name}</span> : null}
              {drilldown.karigar ? <span className="rounded-full bg-brand-light px-3 py-1 text-brand-ink">{t.karigar}: {drilldown.karigar.name}</span> : null}
              {drilldown.counterId ? <span className="rounded-full bg-brand-light px-3 py-1 text-brand-ink">{t.counter}: {counters.find((c) => c.id === drilldown.counterId)?.name ?? data?.byCounter.find((c) => c.counterId === drilldown.counterId)?.name}</span> : null}
              {drilldown.networkMember ? <span className="rounded-full bg-brand-light px-3 py-1 text-brand-ink">{drilldown.networkMember.roleLabel}: {drilldown.networkMember.name}</span> : null}
            </div>
            {drilldownData ? (
              <div className="grid grid-cols-2 gap-2 rounded-lg bg-ink/[0.025] p-3 text-sm sm:grid-cols-4">
                <div><span className="block text-muted">{t.kpiScans}</span><strong className="tabular-nums">{num.format(drilldownData.summary.totalScans)}</strong></div>
                <div><span className="block text-muted">{t.kpiPoints}</span><strong className="tabular-nums">{num.format(drilldownData.summary.totalPoints)}</strong></div>
                <div><span className="block text-muted">{t.kpiKarigars}</span><strong className="tabular-nums">{num.format(drilldownData.summary.activeKhatis)}</strong></div>
                <div><span className="block text-muted">{t.kpiCounters}</span><strong className="tabular-nums">{num.format(drilldownData.summary.activeCounters)}</strong></div>
              </div>
            ) : <Skeleton className="h-16 w-full" />}
            {drilldownError ? (
              <div className="rounded-lg border border-rule p-5 text-sm text-muted">
                <p>{drilldownError}</p>
                <Button className="mt-3" onClick={() => setDrilldownRetry((value) => value + 1)} size="sm" variant="secondary">{t.refresh}</Button>
              </div>
            ) : drilldownData ? (
              <>
                <ProductPerformanceFlow
                  metric={metric}
                  flow={drilldownData.productFlow}
                  focus={drilldown.focus}
                  networkMember={drilldown.networkMember}
                  networkMembers={drilldownData.byNetworkMember}
                  enabledRoles={drilldownData.enabledRoles?.map((role) => role.base)}
                  productId={drilldown.productId}
                  productName={products.find((p) => p.id === drilldown.productId)?.name ?? data?.byProduct.find((p) => p.productId === drilldown.productId)?.name}
                  karigar={drilldown.karigar}
                  counterId={drilldown.counterId}
                  counterName={counters.find((c) => c.id === drilldown.counterId)?.name ?? data?.byCounter.find((c) => c.counterId === drilldown.counterId)?.name}
                  scanCount={drilldownData.summary.totalScans}
                  pointCount={drilldownData.summary.totalPoints}
                  loading={drilldownLoading}
                  onProductSelect={(id) => openDrilldown({
                    ...drilldown,
                    productId: drilldown.productId === id ? "" : id,
                    karigar: drilldown.focus === "counter" || drilldown.focus === "product" ? null : drilldown.karigar,
                    counterId: drilldown.focus === "counter" ? drilldown.counterId : "",
                  })}
                  onKarigarSelect={(value) => openDrilldown({
                    ...drilldown,
                    karigar: drilldown.karigar?.id === value.id ? null : value,
                    counterId: drilldown.focus === "counter" ? drilldown.counterId : "",
                  })}
                  onCounterSelect={(id) => openDrilldown({ ...drilldown, counterId: drilldown.counterId === id ? "" : id })}
                />
                {drilldown.focus === "network" && drilldown.networkMember ? (
                  <section className="overflow-hidden rounded-lg border border-rule bg-raised">
                    <header className="flex flex-wrap items-start justify-between gap-3 border-b border-rule px-4 py-3">
                      <div>
                        <h3 className="font-title text-sm font-semibold text-ink">
                          {networkScopeData?.counter ? "Karigars at this counter" : "Counters in this network"}
                        </h3>
                        <p className="mt-1 text-xs text-muted">
                          {drilldown.networkMember.roleLabel}: {drilldown.networkMember.name}
                          {networkScopeData?.counter ? ` · ${networkScopeData.counter.name}` : " · Organization roster"}
                        </p>
                      </div>
                      {networkScopeData?.counter ? (
                        <Button
                          onClick={() => openDrilldown({ ...drilldown, counterId: "" })}
                          size="sm"
                          variant="secondary"
                        >
                          ← All counters
                        </Button>
                      ) : null}
                    </header>

                    {networkScopeError ? (
                      <div className="p-4 text-sm text-danger">{networkScopeError}</div>
                    ) : networkScopeLoading ? (
                      <div className="space-y-3 p-4">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    ) : networkScopeData?.counter ? (
                      <>
                        <div className="grid grid-cols-2 gap-2 border-b border-rule/70 p-3 sm:grid-cols-4">
                          <div className="rounded-md bg-ink/[0.025] p-3">
                            <span className="block text-xs text-muted">Linked karigars</span>
                            <strong className="mt-1 block tabular-nums">{num.format(networkScopeData.counter.karigarCount)}</strong>
                          </div>
                          <div className="rounded-md bg-ink/[0.025] p-3">
                            <span className="block text-xs text-muted">Scans in selected period</span>
                            <strong className="mt-1 block tabular-nums">{num.format(drilldownData.summary.totalScans)}</strong>
                          </div>
                          <div className="rounded-md bg-ink/[0.025] p-3">
                            <span className="block text-xs text-muted">QCoin in selected period</span>
                            <strong className="mt-1 block tabular-nums">{num.format(drilldownData.summary.totalPoints)}</strong>
                          </div>
                          <div className="rounded-md bg-ink/[0.025] p-3">
                            <span className="block text-xs text-muted">Counter status</span>
                            <span className="mt-1 inline-flex"><Badge tone={statusTone(networkScopeData.counter.status)}>{networkScopeData.counter.status}</Badge></span>
                          </div>
                        </div>
                        {networkScopeData.karigars.length === 0 ? (
                          <EmptyState message="No karigars are currently linked to this counter." title="No karigars" />
                        ) : (
                          <>
                            <Table searchable={false} filterable={false}>
                              <THead>
                                <tr>
                                  <TH>{t.colKarigar}</TH>
                                  <TH>{t.colKyc}</TH>
                                  <TH>{"Status"}</TH>
                                  <TH>{"Joined"}</TH>
                                  <TH align="right">{t.colScans}</TH>
                                  <TH align="right">{t.colPoints}</TH>
                                  <TH align="right">{t.colLast}</TH>
                                </tr>
                              </THead>
                              <tbody>
                                {networkScopeData.karigars.map((person) => {
                                  const performance = drilldownData.byKhati.find((row) => row.khatiId === person.id);
                                  return (
                                    <TR key={person.id}>
                                      <TDStacked primary={person.name} secondary={[person.displayId, person.phone].filter(Boolean).join(" · ") || undefined} />
                                      <TD><Badge tone={statusTone(person.kycStatus ?? "")}>{kycLabel(person.kycStatus)}</Badge></TD>
                                      <TD><Badge tone={statusTone(person.status)}>{person.status}</Badge></TD>
                                      <TD className="whitespace-nowrap text-muted">{person.createdAt ? dateFmt(person.createdAt, { day: "numeric", month: "short", year: "numeric" }) : "—"}</TD>
                                      <TD align="right" numeric>{num.format(performance?.scanCount ?? 0)}</TD>
                                      <TD align="right" numeric>{num.format(performance?.totalPointsEarned ?? 0)}</TD>
                                      <TD align="right" className="whitespace-nowrap text-muted">{lastScan(performance?.lastScanAt ?? null)}</TD>
                                    </TR>
                                  );
                                })}
                              </tbody>
                            </Table>
                            <Pagination
                              onChange={(nextPage) => setNetworkScopePageState({ key: networkScopeKey, page: nextPage })}
                              page={networkScopeData.page}
                              total={networkScopeData.total}
                              totalPages={networkScopeData.totalPages}
                            />
                          </>
                        )}
                      </>
                    ) : networkScopeData ? (
                      networkScopeData.counters.length === 0 ? (
                        <EmptyState message="No counters are currently linked to this network member." title="No counters" />
                      ) : (
                        <>
                          <div className="flex items-center justify-between gap-3 px-4 pt-3 text-sm">
                            <span className="text-muted">{num.format(networkScopeData.total)} counters in this member’s organization scope</span>
                            <span className="text-xs text-muted">Select a counter to view its karigars</span>
                          </div>
                          <Table searchable={false} filterable={false}>
                            <THead>
                              <tr>
                                <TH>{t.colCounter}</TH>
                                <TH>{"Location"}</TH>
                                <TH>{"Status"}</TH>
                                <TH align="right">{t.colKarigars}</TH>
                                <TH align="right">{t.colScans}</TH>
                                <TH align="right">{t.colPoints}</TH>
                              </tr>
                            </THead>
                            <tbody>
                              {networkScopeData.counters.map((counter) => {
                                const performance = drilldownData.byCounter.find((row) => row.counterId === counter.id);
                                return (
                                  <TR key={counter.id} onClick={() => openDrilldown({ ...drilldown, counterId: counter.id })}>
                                    <TDStacked primary={counter.name} secondary={[counter.displayId, counter.phone].filter(Boolean).join(" · ") || undefined} />
                                    <TDStacked primary={[counter.city, counter.state].filter(Boolean).join(", ") || "—"} secondary={counter.address ?? undefined} />
                                    <TD><Badge tone={statusTone(counter.status)}>{counter.status}</Badge></TD>
                                    <TD align="right" numeric>{num.format(counter.karigarCount)}</TD>
                                    <TD align="right" numeric>{num.format(performance?.scanCount ?? 0)}</TD>
                                    <TD align="right" numeric>{num.format(performance?.totalPointsEarned ?? 0)}</TD>
                                  </TR>
                                );
                              })}
                            </tbody>
                          </Table>
                          <Pagination
                            onChange={(nextPage) => setNetworkScopePageState({ key: networkScopeKey, page: nextPage })}
                            page={networkScopeData.page}
                            total={networkScopeData.total}
                            totalPages={networkScopeData.totalPages}
                          />
                        </>
                      )
                    ) : null}
                  </section>
                ) : null}
              </>
            ) : <div className="grid gap-4 lg:grid-cols-3">{[0, 1, 2].map((index) => <Skeleton className="h-48 w-full" key={index} />)}</div>}
          </div>
        ) : null}
      </Modal>
    </Page>
  );
}

function DateInput({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  min?: string | undefined;
  max?: string | undefined;
}) {
  return (
    <label className="flex items-center gap-1.5 text-[13px] text-muted">
      {label}
      <input
        className="h-9 rounded-md border border-rule bg-raised px-2 text-sm text-ink shadow-control focus:border-brand focus:shadow-[0_0_0_3px_var(--gq-color-1-ring)] focus:outline-none"
        max={max}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        type="date"
        value={value}
      />
    </label>
  );
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-brand-light py-0.5 pr-1 pl-2.5 text-[13px] text-brand-ink ring-1 ring-brand-border ring-inset">
      {label}
      <button
        aria-label="Remove"
        className="grid size-5 place-items-center rounded-full hover:bg-brand/15"
        onClick={onRemove}
        type="button"
      >
        <X aria-hidden="true" className="size-3.5" />
      </button>
    </span>
  );
}

/** A share against 100%: the unfilled track is a lighter step of the fill. */
function Meter({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <span className="flex items-center gap-2">
      <span aria-hidden="true" className="h-1.5 flex-1 rounded-full bg-brand-light">
        <span className="block h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
      </span>
      <span className="w-12 text-right text-[13px] tabular-nums text-ink">{value}%</span>
    </span>
  );
}
